import { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import ExpenseModal, { CATEGORY_CONFIG } from '../components/ExpenseModal';
import {
  getAllExpenses,
  deleteExpense,
  getSpendingStats,
  groupByDay,
  groupByMonth,
} from '../services/expenseService';
import type { Expense, ExpenseCategory } from '../types/expense';
import {
  Plus, Search, Pencil, Trash2, Wallet2,
  CalendarDays, TrendingUp, TrendingDown, BarChart2,
  ChevronDown, ChevronUp, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n: number, currency = 'AED') =>
  `${currency} ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDay = (dateStr: string): { label: string; sub: string } => {
  const d = new Date(dateStr + 'T00:00:00');
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  if (dateStr === today.toISOString().split('T')[0]) return { label: 'Today', sub: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) };
  if (dateStr === yesterday.toISOString().split('T')[0]) return { label: 'Yesterday', sub: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) };
  return { label: d.toLocaleDateString('en-US', { weekday: 'long' }), sub: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) };
};

const formatMonth = (monthKey: string): string => {
  const [y, m] = monthKey.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

// ─── Single expense row ───────────────────────────────────────────────────────
const ExpenseRow = ({
  expense, onEdit, onDelete,
}: {
  expense: Expense;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const cfg = CATEGORY_CONFIG[expense.category];
  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-white/3 rounded-xl transition-colors group">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ${cfg.color}`}>
        {cfg.emoji}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">{expense.merchant}</p>
        <p className={`text-xs mt-0.5 ${cfg.text}`}>{cfg.label}</p>
        {expense.note && <p className="text-xs text-gray-600 truncate mt-0.5">{expense.note}</p>}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <p className="text-sm font-bold text-white">{fmt(expense.amount, expense.currency)}</p>
        <div className="hidden group-hover:flex items-center gap-1">
          <button onClick={onEdit} className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/10 transition-colors">
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button onClick={onDelete} className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-400/10 transition-colors">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Day group card ───────────────────────────────────────────────────────────
const DayGroup = ({
  dateStr, expenses, onEdit, onDelete, onAdd,
}: {
  dateStr: string;
  expenses: Expense[];
  onEdit: (e: Expense) => void;
  onDelete: (id: string) => void;
  onAdd: (date: string) => void;
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const total = expenses.reduce((s, e) => s + e.amount, 0);
  const { label, sub } = formatDay(dateStr);

  return (
    <div className="glass-card rounded-2xl overflow-hidden">
      {/* Day header */}
      <div
        className="flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-white/3 transition-colors"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-emerald-500/15 flex items-center justify-center">
            <CalendarDays className="h-4 w-4 text-emerald-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white">{label}</p>
            <p className="text-xs text-gray-500">{sub} · {expenses.length} {expenses.length === 1 ? 'item' : 'items'}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-sm font-bold text-white">{fmt(total)}</p>
          <button
            onClick={(e) => { e.stopPropagation(); onAdd(dateStr); }}
            className="p-1.5 rounded-lg text-emerald-400 hover:bg-emerald-400/10 transition-colors"
            title="Add expense on this day"
          >
            <Plus className="h-4 w-4" />
          </button>
          {collapsed ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronUp className="h-4 w-4 text-gray-500" />}
        </div>
      </div>

      {/* Expenses */}
      {!collapsed && (
        <div className="px-2 pb-2 space-y-0.5 border-t" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
          {expenses.map((e) => (
            <ExpenseRow
              key={e.id}
              expense={e}
              onEdit={() => onEdit(e)}
              onDelete={() => onDelete(e.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
type ViewMode = 'daily' | 'monthly';

const SpendingTracker = () => {
  const [expenses,    setExpenses]    = useState<Expense[]>([]);
  const [stats,       setStats]       = useState({
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    lastMonth: 0,
    categoryBreakdown: {} as Record<ExpenseCategory, number>,
    dailyAvgThisMonth: 0,
  });
  const [modalOpen,   setModalOpen]   = useState(false);
  const [editingExp,  setEditingExp]  = useState<Expense | null>(null);
  const [defaultDate, setDefaultDate] = useState<string | undefined>(undefined);
  const [search,      setSearch]      = useState('');
  const [catFilter,   setCatFilter]   = useState<ExpenseCategory | 'all'>('all');
  const [viewMode,    setViewMode]    = useState<ViewMode>('daily');
  const [showCats,    setShowCats]    = useState(false);

  const refresh = useCallback(async () => {
    const data = await getAllExpenses();
    setExpenses(data);
    setStats(getSpendingStats(data));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Filtered expenses
  const filtered = expenses
    .filter((e) => catFilter === 'all' || e.category === catFilter)
    .filter((e) =>
      !search ||
      e.merchant.toLowerCase().includes(search.toLowerCase()) ||
      (e.note ?? '').toLowerCase().includes(search.toLowerCase())
    );

  const openAdd = (date?: string) => {
    setEditingExp(null);
    setDefaultDate(date ?? new Date().toISOString().split('T')[0]);
    setModalOpen(true);
  };

  const openEdit = (e: Expense) => {
    setEditingExp(e);
    setDefaultDate(undefined);
    setModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    await deleteExpense(id);
    refresh();
  };

  // Month change vs last month
  const monthDelta = stats.lastMonth === 0 ? null : ((stats.thisMonth - stats.lastMonth) / stats.lastMonth) * 100;

  // Category bar chart data
  const catEntries = Object.entries(stats.categoryBreakdown).sort(([, a], [, b]) => b - a) as [ExpenseCategory, number][];
  const maxCat = catEntries[0]?.[1] ?? 1;

  // Daily groups (sorted most-recent first)
  const dayMap = groupByDay(filtered);
  const dayEntries = [...dayMap.entries()].sort(([a], [b]) => b.localeCompare(a));

  // Monthly groups
  const monthMap = groupByMonth(filtered);
  const monthEntries = [...monthMap.entries()].sort(([a], [b]) => b.localeCompare(a));

  return (
    <div className="min-h-screen flex" style={{ background: '#0f0f13' }}>
      <Sidebar />
      <main className="flex-1 min-w-0 overflow-y-auto">
        <div className="p-6 max-w-6xl mx-auto">

          {/* Header */}
          <div className="flex items-start justify-between mb-8 fade-in-up">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-xl bg-emerald-500/15 flex items-center justify-center">
                  <Wallet2 className="h-4 w-4 text-emerald-400" />
                </div>
                <h1 className="text-2xl font-bold text-white">Spending Tracker</h1>
              </div>
              <p className="text-sm text-gray-500">Monitor your daily and monthly expenses</p>
            </div>
            <button
              id="addExpenseBtn"
              onClick={() => openAdd()}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-500 transition-colors shadow-lg shadow-emerald-500/20 flex-shrink-0"
            >
              <Plus className="h-4 w-4" /> Add Expense
            </button>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 fade-in-up">
            {/* Today */}
            <div className="glass-card rounded-2xl p-5">
              <div className="flex justify-between items-start mb-3">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Today</p>
                <div className="w-9 h-9 rounded-xl bg-emerald-500/15 flex items-center justify-center">
                  <CalendarDays className="h-4 w-4 text-emerald-400" />
                </div>
              </div>
              <p className="text-2xl font-bold text-white">{fmt(stats.today)}</p>
              <p className="text-xs text-gray-500 mt-1">Spent today</p>
            </div>

            {/* This week */}
            <div className="glass-card rounded-2xl p-5">
              <div className="flex justify-between items-start mb-3">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">This Week</p>
                <div className="w-9 h-9 rounded-xl bg-indigo-500/15 flex items-center justify-center">
                  <BarChart2 className="h-4 w-4 text-indigo-400" />
                </div>
              </div>
              <p className="text-2xl font-bold text-white">{fmt(stats.thisWeek)}</p>
              <p className="text-xs text-gray-500 mt-1">Since Monday</p>
            </div>

            {/* This month */}
            <div className="glass-card rounded-2xl p-5">
              <div className="flex justify-between items-start mb-3">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">This Month</p>
                <div className="w-9 h-9 rounded-xl bg-violet-500/15 flex items-center justify-center">
                  <TrendingUp className="h-4 w-4 text-violet-400" />
                </div>
              </div>
              <p className="text-2xl font-bold text-white">{fmt(stats.thisMonth)}</p>
              {monthDelta !== null && (
                <div className={`flex items-center gap-1 text-xs mt-1 ${monthDelta > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                  {monthDelta > 0
                    ? <ArrowUpRight className="h-3 w-3" />
                    : <ArrowDownRight className="h-3 w-3" />}
                  {Math.abs(monthDelta).toFixed(1)}% vs last month
                </div>
              )}
              {monthDelta === null && <p className="text-xs text-gray-500 mt-1">No data last month</p>}
            </div>

            {/* Daily avg */}
            <div className="glass-card rounded-2xl p-5">
              <div className="flex justify-between items-start mb-3">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Daily Avg</p>
                <div className="w-9 h-9 rounded-xl bg-amber-500/15 flex items-center justify-center">
                  <TrendingDown className="h-4 w-4 text-amber-400" />
                </div>
              </div>
              <p className="text-2xl font-bold text-white">{fmt(stats.dailyAvgThisMonth)}</p>
              <p className="text-xs text-gray-500 mt-1">Per day this month</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            {/* Category breakdown */}
            <div className="glass-card rounded-2xl p-6 fade-in-up delay-200">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-sm font-semibold text-white">By Category</h2>
                <span className="text-xs text-gray-500">this month</span>
              </div>
              {catEntries.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">No expenses yet.</p>
              ) : (
                <div className="space-y-3">
                  {catEntries.map(([cat, total]) => {
                    const cfg = CATEGORY_CONFIG[cat];
                    return (
                      <div key={cat}>
                        <div className="flex justify-between text-xs mb-1.5">
                          <div className="flex items-center gap-1.5">
                            <span>{cfg.emoji}</span>
                            <span className={cfg.text}>{cfg.label}</span>
                          </div>
                          <span className="text-white font-medium">{fmt(total)}</span>
                        </div>
                        <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{ width: `${(total / maxCat) * 100}%`, background: cfg.text.replace('text-', '').replace('-400', '') }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Right: last month comparison */}
            <div className="glass-card rounded-2xl p-6 fade-in-up delay-300 lg:col-span-2">
              <h2 className="text-sm font-semibold text-white mb-5">Month Comparison</h2>
              <div className="space-y-5">
                {[
                  { label: 'This Month', value: stats.thisMonth, color: '#a78bfa' },
                  { label: 'Last Month', value: stats.lastMonth, color: '#6b7280' },
                ].map(({ label, value, color }) => (
                  <div key={label}>
                    <div className="flex justify-between text-xs mb-2">
                      <span className="text-gray-400">{label}</span>
                      <span className="text-white font-medium">{fmt(value)}</span>
                    </div>
                    <div className="h-3 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${Math.min(100, stats.thisMonth + stats.lastMonth === 0 ? 0 : (value / Math.max(stats.thisMonth, stats.lastMonth)) * 100)}%`,
                          background: color,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {/* Diff callout */}
              <div className={`mt-6 p-4 rounded-xl ${
                monthDelta === null ? 'bg-white/3' :
                monthDelta > 0 ? 'bg-red-500/10 border border-red-500/20' :
                'bg-emerald-500/10 border border-emerald-500/20'
              }`}>
                {monthDelta === null ? (
                  <p className="text-sm text-gray-400">Add more expenses to unlock month-over-month comparison.</p>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-white">
                        {monthDelta > 0 ? '📈 Spending up this month' : '📉 Spending down this month'}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {fmt(Math.abs(stats.thisMonth - stats.lastMonth))} {monthDelta > 0 ? 'more' : 'less'} than last month
                      </p>
                    </div>
                    <p className={`text-lg font-bold ${monthDelta > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                      {monthDelta > 0 ? '+' : ''}{monthDelta.toFixed(1)}%
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-5 fade-in-up delay-300">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <input
                className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500"
                placeholder="Search by merchant or note…"
                value={search} onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {/* Category filter */}
            <div className="relative">
              <button
                onClick={() => setShowCats(!showCats)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-gray-400 hover:border-white/20 transition-colors"
              >
                {catFilter === 'all' ? '📦 All categories' : `${CATEGORY_CONFIG[catFilter].emoji} ${CATEGORY_CONFIG[catFilter].label}`}
                <ChevronDown className={`h-4 w-4 transition-transform ${showCats ? 'rotate-180' : ''}`} />
              </button>
              {showCats && (
                <div className="absolute right-0 top-full mt-1 w-52 rounded-xl shadow-2xl z-10 py-2"
                  style={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <button
                    onClick={() => { setCatFilter('all'); setShowCats(false); }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-white/5 transition-colors ${catFilter === 'all' ? 'text-white' : 'text-gray-400'}`}
                  >
                    📦 All categories
                  </button>
                  {Object.entries(CATEGORY_CONFIG).map(([cat, cfg]) => (
                    <button key={cat}
                      onClick={() => { setCatFilter(cat as ExpenseCategory); setShowCats(false); }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-white/5 transition-colors ${catFilter === cat ? cfg.text : 'text-gray-400'}`}>
                      {cfg.emoji} {cfg.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* View toggle */}
            <div className="flex gap-1 p-1 rounded-xl bg-white/5 border border-white/10">
              <button onClick={() => setViewMode('daily')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${viewMode === 'daily' ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'}`}>
                <CalendarDays className="h-3.5 w-3.5" /> Daily
              </button>
              <button onClick={() => setViewMode('monthly')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${viewMode === 'monthly' ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'}`}>
                <BarChart2 className="h-3.5 w-3.5" /> Monthly
              </button>
            </div>
          </div>

          {/* Expense list */}
          {filtered.length === 0 ? (
            <div className="glass-card rounded-2xl p-12 text-center fade-in-up">
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                <Wallet2 className="h-7 w-7 text-emerald-400" />
              </div>
              <p className="text-white font-medium mb-1">{search || catFilter !== 'all' ? 'No results found' : 'No expenses yet'}</p>
              <p className="text-sm text-gray-500">
                {search || catFilter !== 'all' ? 'Adjust your filters.' : 'Click "Add Expense" to log your first one.'}
              </p>
            </div>
          ) : viewMode === 'daily' ? (
            <div className="space-y-4 fade-in-up delay-400">
              {dayEntries.map(([date, exps]) => (
                <DayGroup
                  key={date}
                  dateStr={date}
                  expenses={exps}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  onAdd={openAdd}
                />
              ))}
            </div>
          ) : (
            /* Monthly view */
            <div className="space-y-4 fade-in-up delay-400">
              {monthEntries.map(([monthKey, exps]) => {
                const total = exps.reduce((s, e) => s + e.amount, 0);
                const catSums: Partial<Record<ExpenseCategory, number>> = {};
                for (const e of exps) catSums[e.category] = (catSums[e.category] ?? 0) + e.amount;
                const topCats = Object.entries(catSums).sort(([, a], [, b]) => b - a).slice(0, 3) as [ExpenseCategory, number][];

                return (
                  <div key={monthKey} className="glass-card rounded-2xl overflow-hidden">
                    <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                      <div>
                        <p className="text-sm font-semibold text-white">{formatMonth(monthKey)}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{exps.length} transactions</p>
                      </div>
                      <p className="text-base font-bold text-white">{fmt(total)}</p>
                    </div>
                    {/* Category mini-breakdown */}
                    <div className="px-5 py-4 space-y-3">
                      {topCats.map(([cat, amount]) => {
                        const cfg = CATEGORY_CONFIG[cat];
                        return (
                          <div key={cat} className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                              <span>{cfg.emoji}</span>
                              <span className={cfg.text}>{cfg.label}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="w-24 h-1.5 bg-white/5 rounded-full overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${(amount / total) * 100}%`, background: cfg.text.replace('text-', '').replace('-400', '') }} />
                              </div>
                              <span className="text-white font-medium w-20 text-right">{fmt(amount)}</span>
                            </div>
                          </div>
                        );
                      })}
                      {Object.keys(catSums).length > 3 && (
                        <p className="text-xs text-gray-600">+{Object.keys(catSums).length - 3} more categories</p>
                      )}
                    </div>
                    {/* Expense rows */}
                    <div className="px-2 pb-2 border-t space-y-0.5" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                      {exps
                        .sort((a, b) => b.date.localeCompare(a.date))
                        .map((e) => (
                          <ExpenseRow key={e.id} expense={e} onEdit={() => openEdit(e)} onDelete={() => handleDelete(e.id)} />
                        ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <ExpenseModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={refresh}
        editingExpense={editingExp}
        defaultDate={defaultDate}
      />
    </div>
  );
};

export default SpendingTracker;
